import { useCalculateReAssessment } from "@/api/console/re-assessments/useCalculateReAssessment";
import { useGetInterviewRecord } from "@/api/console/re-assessments/useGetInterviewRecord";
import { useGetReAssessment } from "@/api/console/re-assessments/useGetReAssessment";
import { useUpdateInterviewRecord } from "@/api/console/re-assessments/useUpdateInterviewRecord";
import AdminContentContainer from "@/components/AdminContentContainer";
import AdminNavbar from "@/components/AdminNavbar/AdminNavbar";
import BottomBar from "@/components/BottomBar";
import LoadingScreen from "@/components/Loading";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { STATUS } from "@/config/progress";
import { handleCompanyUserName } from "@/lib/handleName";
import toast from "@/lib/toast";
import {
  InterviewRecordUpdate,
  interviewRecordUpdateSchema,
} from "@/schema/console/interview-records/InterviewRecord";
import Heading from "@/src/console/(auth)/(check-assessments)/re-assessments/[id]/_components/Heading";
import InterviewRecord from "@/src/console/(auth)/(check-assessments)/re-assessments/[id]/_components/InterviewRecord";
import AssessmentInfo from "@/src/console/(auth)/_components/CheckingAssessment/AssessmentInfo";
import CategoriesSection from "@/src/console/(auth)/_components/CheckingAssessment/CategoriesSection/CategoriesSection";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

function ReAssessmentDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const breadcrumbs = [
    {
      name: t("re-assessments"),
      href: "/console/check-submissions",
    },
    {
      name: t("assessment-details"),
      href: location.pathname,
    },
  ];

  const { id = "" } = useParams();
  const { data, isLoading } = useGetReAssessment(id);

  const { trigger, isMutating } = useCalculateReAssessment(id, {
    onSuccess: (data) => {
      toast.success(data.message);
      const statues = [
        STATUS.ASSESSMENT_COMPLETED,
        STATUS.RE_ASSESSMENT,
        STATUS.RE_ASSESSMENT_GRADED_COMPLETED,
        STATUS.RE_ASSESSMENT_EVALUATION_COMPLETED,
      ];
      mutate(`/console/assessment_progresses?status=${statues.join(",")}`);
      navigate("/console/re-assessments");
    },
  });

  const {
    data: interviewRecord,
    isLoading: isInterviewLoading,
    mutate: refetchInterviewRecord,
  } = useGetInterviewRecord(id, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateOnMount: true,
  });

  const { trigger: updateInterviewRecord, isMutating: isUpdating } =
    useUpdateInterviewRecord(id, {
      onSuccess: (data) => {
        toast.success(data.message);
        refetchInterviewRecord();
      },
    });

  const methods = useForm<InterviewRecordUpdate>({
    resolver: zodResolver(interviewRecordUpdateSchema),
    values: interviewRecord?.data,
  });

  if (isLoading || !data || isInterviewLoading || !interviewRecord)
    return <LoadingScreen />;

  const {
    answer_categories,
    company,
    company_document_files,
    submitted_by,
    submitted_at,
  } = data.data;
  const disabled = answer_categories.some((category) => !category.is_checked);

  const { updated_at, updated_by } = interviewRecord.data;

  return (
    <Tabs defaultValue="categories">
      <AdminNavbar breadcrumbs={breadcrumbs} />
      <AdminContentContainer
        bottomBar={<BottomBar backLink="/console/re-assessments" />}
      >
        <Heading
          isConfirming={isMutating}
          disabled={disabled}
          onConfirm={() => {
            toast.loading(t("confirm-assessment-loading"));
            trigger();
          }}
        />
        <div className="m-6">
          <TabsContent value="categories">
            <CategoriesSection data={answer_categories} />
          </TabsContent>
          <TabsContent value="interview record">
            <FormProvider {...methods}>
              <InterviewRecord
                isUpdating={isUpdating}
                updateInterviewRecord={() => {
                  methods.handleSubmit((data) => {
                    toast.loading(t("save-changes-loading"));
                    updateInterviewRecord(data);
                  })();
                }}
                updated_at={updated_at}
                updated_by={updated_by}
              />
            </FormProvider>
          </TabsContent>
          <TabsContent value="assessment info">
            <AssessmentInfo
              companyInfo={company}
              documents={company_document_files}
              submittedAt={submitted_at}
              submittedBy={handleCompanyUserName(
                submitted_by.id,
                submitted_by.name,
              )}
            />
          </TabsContent>
        </div>
      </AdminContentContainer>
    </Tabs>
  );
}

export default ReAssessmentDetail;
